<template>
  <div class="settings-page">
    <h2 class="page-title">系统设置</h2>

    <el-card v-loading="loading" class="block">
      <div slot="header">人脸相似度</div>
      <p class="desc">数值越高识别越严格（推荐 30–50）</p>
      <el-alert
        :title="engineOnline ? '人脸引擎运行正常' : (engineMessage || '人脸引擎未启动，刷脸签到将不可用')"
        :type="engineOnline ? 'success' : 'warning'"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
      />
      <div class="slider-row">
        <span class="label">相似度阈值</span>
        <el-slider v-model="thresholdPercent" :min="10" :max="95" show-input />
      </div>
      <div class="tip">当前值：{{ thresholdPercent }}%，签到时相似度低于此值将识别失败</div>
      <el-button type="primary" :loading="saving" @click="saveSettings">保存阈值</el-button>
    </el-card>

    <el-card v-loading="exchangeLoading" class="block">
      <div slot="header">大课抵小课（默认比例）</div>
      <p class="desc">
        开通后记下「N 节大课 = 1 节小课」比例。签到时由教练选择划小课还是抵扣；小课不够默认记欠，不会自动用大课抵。
        学员可在报名或充值时单独开通，此处仅设置默认比例。
      </p>
      <el-form label-width="140px" style="max-width:420px">
        <el-form-item label="默认兑换比例">
          <el-input-number v-model="exchangeDefaultRate" :min="2" :max="10" :step="1" />
          <span class="rate-suffix">节大课 = 1 节小课（可设 2～10）</span>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="exchangeSaving" @click="saveExchangeSettings">保存默认比例</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-loading="hoursLowLoading" class="block" id="hours-low-card">
      <div slot="header">课时不足自动提醒</div>
      <p class="desc">
        剩余大课课时 ≤ 阈值时自动提醒家长（站内；开通短信后同步发短信）。
        文案可自定义，占位符：<code>{name}</code>=学员姓名，<code>{remaining}</code>=剩余课时。
      </p>
      <el-form label-width="140px" style="max-width:640px">
        <el-form-item label="剩余课时 ≤">
          <el-input-number v-model="hoursLowThreshold" :min="0" :max="50" />
          <span style="margin-left:8px;color:#64748B">节</span>
        </el-form-item>
        <el-form-item label="提醒文案模板">
          <el-input
            type="textarea"
            v-model="hoursLowTemplate"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="{name} 学员已剩余{remaining}节课，请联系管理员充值"
          />
          <div class="tpl-preview">预览：{{ hoursLowPreview }}</div>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="hoursLowSaving" @click="saveHoursLowSettings">保存提醒设置</el-button>
          <el-button type="text" @click="$router.push({ path: '/notify', query: { scope: 'low_hours' } })">
            通知课时不足家长
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="block">
      <div slot="header">家长初始密码</div>
      <p class="desc">
        新报名家长用这个密码登录。不要用 123456。改这里只影响以后新报名的家长，已经登录过的家长请让他们自己改密。
      </p>
      <el-form label-width="100px" style="max-width:420px">
        <el-form-item label="新初始密码">
          <el-input v-model="parentDefaultPwd" type="password" show-password placeholder="至少6位，勿用弱密码" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="parentPwdSaving" @click="saveParentDefaultPwd">保存家长初始密码</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="block">
      <div slot="header">修改管理员密码</div>
      <p class="desc">建议首次登录后立即修改默认密码，新密码至少 6 位。</p>
      <el-form label-width="100px" style="max-width:420px">
        <el-form-item label="原密码">
          <el-input v-model="pwdForm.oldPassword" type="password" show-password />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="pwdForm.newPassword" type="password" show-password />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="pwdSaving" @click="changePwd">保存新密码</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="block">
      <div slot="header">现场签到入口</div>
      <p class="desc">
        链接已带上当前俱乐部编码。建议用手机浏览器打开，并加入主屏幕。
      </p>
      <div class="link-box">
        <div>普通入口：<a :href="checkinUrl" target="_blank">{{ checkinUrl }}</a></div>
        <div>大屏/平板（无底栏）：<a :href="kioskUrl" target="_blank">{{ kioskUrl }}</a></div>
      </div>
      <el-button type="success" plain @click="openKiosk">打开现场签到页</el-button>
    </el-card>

    <el-card class="block">
      <div slot="header">签到口令</div>
      <p class="desc">
        未登录管理员时，进入签到页需输入口令（防外人乱刷）。口令保存在本机浏览器，请设置至少 6 位，勿使用默认口令。
      </p>
      <el-input v-model="pin" placeholder="签到口令（至少6位）" style="max-width: 280px" show-password />
      <div style="margin-top: 12px">
        <el-button type="primary" @click="savePin">保存口令到本机</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getFaceSettings, updateFaceSettings, changeAdminPassword, updateParentDefaultPassword, getPrivateExchangeSettings, updatePrivateExchangeSettings, getHoursLowSettings, updateHoursLowSettings } from '../api'

const PIN_KEY = 'clockout_checkin_pin'

export default {
  name: 'Settings',
  data() {
    return {
      thresholdPercent: 40,
      engineOnline: false,
      engineMessage: '',
      loading: false,
      saving: false,
      exchangeLoading: false,
      exchangeSaving: false,
      exchangeDefaultRate: 2,
      hoursLowLoading: false,
      hoursLowSaving: false,
      hoursLowThreshold: 3,
      hoursLowTemplate: '{name} 学员已剩余{remaining}节课，请联系管理员充值',
      pwdSaving: false,
      pwdForm: { oldPassword: '', newPassword: '' },
      parentDefaultPwd: '',
      parentPwdSaving: false,
      pin: localStorage.getItem(PIN_KEY) || '',
      origin: typeof window !== 'undefined' ? window.location.origin : ''
    }
  },
  computed: {
    hoursLowPreview() {
      const tpl = this.hoursLowTemplate || ''
      return tpl
        .replace(/\{name\}/g, '孟佳宜')
        .replace(/\{remaining\}/g, String(this.hoursLowThreshold != null ? this.hoursLowThreshold : 3))
    },
    clubCode() {
      return localStorage.getItem('club_code') || ''
    },
    checkinUrl() {
      return `${this.origin}/#/checkin?clubCode=${encodeURIComponent(this.clubCode)}`
    },
    kioskUrl() {
      return `${this.origin}/#/kiosk?clubCode=${encodeURIComponent(this.clubCode)}`
    }
  },
  mounted() {
    this.loadSettings()
    this.loadExchangeSettings()
    this.loadHoursLowSettings()
    if (this.$route.query && this.$route.query.focus === 'hours-low') {
      this.$nextTick(() => {
        const el = document.getElementById('hours-low-card')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  },
  methods: {
    async loadSettings() {
      this.loading = true
      try {
        const res = await getFaceSettings()
        this.thresholdPercent = res.data.similarityThresholdPercent || 40
        this.engineOnline = !!(res.data && res.data.engineOnline)
        this.engineMessage = (res.data && res.data.engineMessage) || ''
      } finally {
        this.loading = false
      }
    },
    async loadExchangeSettings() {
      this.exchangeLoading = true
      try {
        const res = await getPrivateExchangeSettings()
        const rate = (res.data && res.data.defaultRate) || 2
        this.exchangeDefaultRate = rate >= 2 ? rate : 2
      } finally {
        this.exchangeLoading = false
      }
    },
    async loadHoursLowSettings() {
      this.hoursLowLoading = true
      try {
        const res = await getHoursLowSettings()
        const n = res.data && res.data.threshold
        this.hoursLowThreshold = (n === 0 || n) ? n : 3
        this.hoursLowTemplate = (res.data && res.data.template)
          || '{name} 学员已剩余{remaining}节课，请联系管理员充值'
      } finally {
        this.hoursLowLoading = false
      }
    },
    async saveSettings() {
      this.saving = true
      try {
        const res = await updateFaceSettings(this.thresholdPercent)
        this.thresholdPercent = res.data.similarityThresholdPercent
        this.$message.success('保存成功')
      } finally {
        this.saving = false
      }
    },
    async saveExchangeSettings() {
      this.exchangeSaving = true
      try {
        const res = await updatePrivateExchangeSettings(this.exchangeDefaultRate)
        this.exchangeDefaultRate = (res.data && res.data.defaultRate) || this.exchangeDefaultRate
        this.$message.success('默认兑换比例已保存')
      } finally {
        this.exchangeSaving = false
      }
    },
    async saveHoursLowSettings() {
      this.hoursLowSaving = true
      try {
        const res = await updateHoursLowSettings(this.hoursLowThreshold, this.hoursLowTemplate)
        const n = res.data && res.data.threshold
        this.hoursLowThreshold = (n === 0 || n) ? n : this.hoursLowThreshold
        if (res.data && res.data.template) {
          this.hoursLowTemplate = res.data.template
        }
        this.$message.success('课时不足提醒设置已保存')
      } finally {
        this.hoursLowSaving = false
      }
    },
    async saveParentDefaultPwd() {
      const pwd = String(this.parentDefaultPwd || '').trim()
      if (pwd.length < 6) {
        return this.$message.warning('家长初始密码至少6位')
      }
      this.parentPwdSaving = true
      try {
        await updateParentDefaultPassword(pwd)
        this.parentDefaultPwd = ''
        this.$message.success('家长初始密码已更新，仅对新报名家长生效')
      } finally {
        this.parentPwdSaving = false
      }
    },
    async changePwd() {
      if (!this.pwdForm.oldPassword || !this.pwdForm.newPassword) {
        return this.$message.warning('请填写原密码和新密码')
      }
      if (this.pwdForm.newPassword.length < 6) {
        return this.$message.warning('新密码至少6位')
      }
      this.pwdSaving = true
      try {
        await changeAdminPassword(this.pwdForm)
        this.$message.success('密码已修改')
        this.pwdForm = { oldPassword: '', newPassword: '' }
      } finally {
        this.pwdSaving = false
      }
    },
    savePin() {
      const v = String(this.pin || '').trim()
      if (v.length < 6) {
        this.$message.warning('口令至少6位')
        return
      }
      if (v === '8888') {
        this.$message.warning('请勿使用过于简单的默认口令')
        return
      }
      localStorage.setItem(PIN_KEY, v)
      this.$message.success('口令已保存到本机浏览器')
    },
    openKiosk() {
      window.open(this.kioskUrl, '_blank')
    }
  }
}
</script>

<style scoped>
.page-title { margin-bottom: 20px; }
.block { margin-bottom: 20px; }
.desc { color: #666; margin-bottom: 16px; font-size: 14px; line-height: 1.6; }
.rate-suffix { margin-left: 8px; color: #64748b; font-size: 13px; }
.tpl-preview { margin-top: 8px; color: #0F766E; font-size: 13px; line-height: 1.5; }
.slider-row { margin-bottom: 16px; }
.label { display: block; margin-bottom: 12px; font-weight: bold; }
.tip { color: #999; font-size: 13px; margin-bottom: 20px; }
.link-box {
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.8;
  margin-bottom: 14px;
  word-break: break-all;
}
.link-box a { color: #0f766e; }
</style>
